import { FolderOpen, ChevronRight, DollarSign, Plus, Calendar } from "lucide-react";
import { useState } from "react";
import type { Artist, TabId } from "../data";
import { ContentPanel, MetricCard, StatusPill } from "./shared";
import StartProjectModal from "./StartProjectModal";

interface Props {
  selectedArtist: Artist;
  setActiveTab: (tab: TabId) => void;
  onOpenProject: (projectId: string) => void;
}

const COMPLETED_STATUSES = ["completed"];

export default function ArtistOverview({ selectedArtist, setActiveTab, onOpenProject }: Props) {
  const [showStartProject, setShowStartProject] = useState(false);

  const activeProjects = selectedArtist.projects.filter(
    (p) => !COMPLETED_STATUSES.includes(p.status ?? "in_progress"),
  );

  const fmt = new Intl.NumberFormat("en-AU", {
    currency: "AUD",
    maximumFractionDigits: 2,
    style: "currency",
  });

  const balanceCents = selectedArtist.balanceCents ?? 0;
  const hasBalance = balanceCents > 0;

  return (
    <div className="space-stack">
      <section className="hero-panel">
        <div>
          <p className="eyebrow">Artist portal</p>
          <h1>{selectedArtist.name}</h1>
          <p className="hero-subtitle">Access your project files and more</p>
        </div>
        <div className="hero-actions">
          <button
            className="primary-link"
            onClick={() => setShowStartProject(true)}
            type="button"
          >
            <Plus className="h-4 w-4" />
            Start New Project
          </button>
          <a
            className="primary-link"
            href="https://johnsonboibeats.com/book"
            rel="noreferrer"
            target="_blank"
          >
            <Calendar className="h-4 w-4" />
            Book studio
          </a>
        </div>
      </section>

      <section className="metric-grid">
        <MetricCard
          icon={<FolderOpen className="h-5 w-5" />}
          label="Projects"
          value={selectedArtist.projects.length.toString()}
        />
        <MetricCard
          icon={<FolderOpen className="h-5 w-5" />}
          label="Active"
          value={activeProjects.length.toString()}
        />
        <MetricCard
          icon={<DollarSign className="h-5 w-5" />}
          label="Open balance"
          value={fmt.format(balanceCents / 100)}
        />
      </section>

      {activeProjects.length > 0 ? (
        <ContentPanel eyebrow="Current projects" title={`${activeProjects.length} active`}>
          <div className="current-projects-list">
            {activeProjects.map((p) => (
              <div className="active-project-card" key={p.id}>
                <div className="active-project-info">
                  <strong>{p.title}</strong>
                  <StatusPill status={p.status} />
                  {p.brief ? (
                    <p className="active-project-brief">{p.brief}</p>
                  ) : (
                    <p className="active-project-brief">
                      Open this project to preview mixes, stems, and leave timed comments.
                    </p>
                  )}
                </div>
                <div className="active-project-actions">
                  <button
                    className="primary-link"
                    onClick={() => onOpenProject(p.id)}
                    type="button"
                  >
                    <ChevronRight className="h-4 w-4" />
                    Review project
                  </button>
                </div>
              </div>
            ))}
          </div>
        </ContentPanel>
      ) : selectedArtist.projects.length === 0 ? (
        <ContentPanel eyebrow="Getting started" title="No projects yet">
          <p className="empty-state">
            No projects on your account yet. Start a new project to get going.
          </p>
        </ContentPanel>
      ) : null}

      {hasBalance ? (
        <ContentPanel title="Open balance" eyebrow="Payment">
          <p className="balance-hint" style={{ marginTop: 0 }}>
            You have an open balance of {fmt.format(balanceCents / 100)}. Open a project to pay.
          </p>
        </ContentPanel>
      ) : null}

      {showStartProject ? (
        <StartProjectModal
          artists={[selectedArtist]}
          isAdmin={false}
          onClose={() => setShowStartProject(false)}
        />
      ) : null}
    </div>
  );
}
